import {_, React, observer, observable, computed, action, classNames, useAsObservableSource, Fragment, IDisposer} from '@api'
import {DynamicRangeSlider, RangeInput} from '@appbaseio/reactivesearch'
import {RangeInputProps} from '@appbaseio/reactivesearch/lib/components/range/RangeInput'
import {Slider} from '@material-ui/core'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {FilterGroupUIStore, useFilterGroupUIStore} from '@modules/catalog/internal/filters/components/internal/FilterGroup'
import {StoresInstance, ui} from '@stores'
import {autorun, reaction, runInAction, toJS} from 'mobx'
import {useEffect} from 'react'
import * as css from './FilterGroupRanges.css'

/// region Store

export class FilterGroupRangesStore extends VipLocalStore {
  constructor(public props: Partial<MyProps> & { groupStore: FilterGroupUIStore}, stores: StoresInstance) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun( () => {
        if (this.filter.search) {
          runInAction(() => {
            if (this.state?.range?.start === null && this.state?.range?.end === null) {
              this.state.range = null
            }
          })
        }}, {name: `Reset null range when search text changes`}),
      autorun(() => {
        const {data, min, max, state} = this

        if (data && min === null && max === null) {
          runInAction(() => state.range = {start: null, end: null})
        }
        else if (state?.range) {
          const {range: {start, end}} = state
          if (start === null && end === null) {
            runInAction(() => {
              state.range = {start: min, end: max}
            })
          }
        }

        if (data) {
          runInAction(() => {
            this.value = [min, max]
          })
        }
      }, { name: `Set filter range from data`}),
      reaction(() => this.data != null, () => this.initializeRangeValueFromMinMax())
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get isDisabled() {
    const {data, min, max} = this
    return data != null && min === null && max === null
  }

  @action initializeRangeValueFromMinMax = () => {
    if (this.data) {
      const {min, max} = this
      this.value = [min, max]
    }

  }

  @computed get min() {
    return this.data?.min?.value ?? null

  }

  @computed get max() {
    return this.data?.max?.value ?? null
  }

  @observable value = [null, null]
  getLabel = (value, index) => {
    return `${value} - ${index}`
  }

  @action onChange = (e, newValue: number[]) => {
    this.value = newValue
  }

  @computed get state() {
    const {filter: {stateFor}, props: {group}} = this
    return stateFor(group)
  }

  @action onCustomQuery = (value, props) => {
    const r = this.catalog.catalogItemFilter.filter
    // console.log({value, props, r})

    return r
  }

  get data() {
    return this.props.groupStore.data
  }

  @action onQueryChange = (prev, cur) => {
    // console.log(cur, this.state)
    this.state.customQuery = cur?.query
  }

  @action onValidateRange = ([start, end]) => {
    const {min, max} = this
    if (start < min) return false
    if (end > max) return false
    //console.log('validate', {start, end})
    return true
  }
  @action onBeforeValueChange = (a, b) => {
    // console.log(a, b)
  }

  @action onValueChange = (value: { start: number, end: number } | Array<number>) => {
    const {state, props: {group}, filter: {rangeQueryTermFor}} = this,
      range = _.isArray(value) ? { start: value[0], end: value[1]} : value

    if ((range.start == this.rangeInputProps.range.start) && range.end == this.rangeInputProps.range.end) {
      state.range = null
      state.customQuery = null
    }
    else {
      state.customQuery = rangeQueryTermFor({...range, field: group})
      state.range = range
    }
    // console.log({...range, q: state.customQuery})
  }

  @action onValueChange_debounce = _.debounce((range: { start: number, end: number } | Array<number>) => this.onValueChange(range), 10, {leading: true, trailing: true})

  @observable showHistogram = false

  @computed get rangeInputProps() {
    const {min, max, showHistogram, onValidateRange, props: {group}, state: {def}, onBeforeValueChange, onValueChange, onValueChange_debounce, onQueryChange, state} = this,
      range = {
        start: (_.isNaN(state.range?.start) ? min : state.range?.start) ?? min,
        end: (_.isNaN(state.range?.end) ? max : state.range?.end) ?? max,
      }
        return {
      dataField: group,
      componentId: `${group}-RangeInput`,
      //title:null,
      showHistogram,
      includeNullValues: true,
      range: {
        start: min,
        end: max
      },
      stepValue: 1, // Need a better value here or way to set it in config
      value: range,
      className: css.rangeInput,
      innerClass: {
        title: css.title,
        slider: css.slider,
        label: css.label

      },
      // rangeLabels: {
      //   start: `${ui.htmlForCurrency(def?.currency)}${min.toLocaleString()}`,
      //   end: `${ui.htmlForCurrency(def?.currency)}${max.toLocaleString()}`,
      // },
      beforeValueChange: onBeforeValueChange,
      onValueChange: onValueChange_debounce,
      onChange: onValueChange,
      onQueryChange: onQueryChange,
      validateRange: onValidateRange,
      style: {}
    } as Partial<RangeInputProps>
  }
}

export const FilterGroupRangesStoreContext = React.createContext<FilterGroupRangesStore>(null)
export const useFilterGroupRangesStore = () => React.useContext(FilterGroupRangesStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  group?: string
}

export const FilterGroupRanges = observer<MyProps>(function FilterGroupRanges({className, ...props}) {
  const groupStore = useFilterGroupUIStore(),
    obsProps = useAsObservableSource({group: props.group, groupStore}),
    store = useVipLocalStore((stores) => new FilterGroupRangesStore(obsProps, stores)),
    {showHistogram, min, max, isDisabled, rangeInputProps, registerEffects, value, onValueChange, onChange, data, getLabel, state, state: {def, expanded}, props: {group}} = store

  useEffect(() => registerEffects(), [])

  // console.log(toJS(rangeInputProps))

  return (
    <FilterGroupRangesStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           data-currency={def?.currency}
           data-histogram={showHistogram}
           data-disabled={isDisabled}
           data-min={min} data-max={max}
      >
        {isDisabled ? <div className={css.noData}>No data</div>
          : <Fragment>
           {/*<Typography id="range-slider" gutterBottom>*/}
           {/*  Some Label*/}
           {/*</Typography>*/}

           {expanded && data && <Fragment>
             {false && <DynamicRangeSlider
				 componentId={group}
				 dataField={group}
				 showHistogram={showHistogram}
				 showFilter={true}
				 className={css.rangeInput}
				 includeNullValues
         queryFormat='basic_date_time'
         calendarInterval='week'
				 innerClass={{
                   title: css.title,
                   slider: css.slider,
                   label: css.label
                 }}
				 defaultValue={(min, max) => ({start: Math.max(min, state?.range?.start ?? 0), end: Math.min(max, state?.range?.end ?? max)})}
				 onValueChange={onValueChange}
			 />}

             <RangeInput {...rangeInputProps as any} />
		   </Fragment>}

           {false && <Slider
			   value={value}
			   color='secondary'
			   onChange={onChange}
			   valueLabelDisplay='auto'
			   aria-labelledby='range-slider'
			   getAriaValueText={getLabel}
		   />}
         </Fragment>}
      </div>
    </FilterGroupRangesStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/filters/components/internal/FilterGroupRanges.tsx